import {
  Calendar,
  Flag,
  IdCard,
  MapPin,
  Pencil,
  Tag,
  type LucideIcon
} from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { ForeignNational } from "../../../../api/types/foreignNational";
import { SectionCard } from "../../components/SectionCard";
import { expirationUrgency } from "../../header/clientHeaderModel";
import { formatMdY } from "../../lib/format";

export const PERSONAL_SECTION_IDS = {
  contactInfo: "personal-contact-info",
  immigrationInformation: "personal-immigration-information",
  expirationDates: "personal-expiration-dates",
  currentAddress: "personal-current-address"
} as const;

type Fact = {
  icon: LucideIcon;
  label: string;
  value: string;
  date?: string | null;
};

const FactRow: FC<Fact> = ({ icon: Icon, label, value, date }) => {
  const urgency = date ? expirationUrgency(date) : "none";
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
        <Icon className="size-3.5 shrink-0 opacity-70" />
        <span className="truncate">{label}</span>
      </span>
      <span
        className={cn(
          "shrink-0 text-xs font-medium tabular-nums",
          urgency === "expired" && "text-danger",
          urgency === "soon" && "text-warn"
        )}
      >
        {value}
      </span>
    </div>
  );
};

const EditButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button
    variant="ghost"
    size="xs"
    className="text-muted-foreground hover:text-foreground"
    onClick={onClick}
  >
    <Pencil /> Edit
  </Button>
);

const Loading: FC = () => (
  <div className="flex flex-col gap-2 py-1">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex items-center justify-between">
        <Skeleton className="h-3 w-28 rounded-sm" />
        <Skeleton className="h-3 w-20 rounded-sm" />
      </div>
    ))}
  </div>
);

type Props = {
  client: ForeignNational | null;
  isLoading: boolean;
  onOpenPersonalSection: (sectionId: string) => void;
};

/**
 * Key immigration facts and expiration dates, each linking into Personal
 * Information. Two stacked sections sharing one hairline, rendered as a cell
 * of the Overview's hairline grid.
 */
export const ApplicantSummaryCard: FC<Props> = ({
  client,
  isLoading,
  onOpenPersonalSection
}) => {
  const str = (v: unknown) => (typeof v === "string" ? v.trim() : "") || "-";

  return (
    <div className="flex flex-col gap-3">
      <SectionCard
        title="Applicant Summary"
        action={
          <EditButton
            onClick={() =>
              onOpenPersonalSection(PERSONAL_SECTION_IDS.immigrationInformation)
            }
          />
        }
      >
        {isLoading || !client ? (
          <Loading />
        ) : (
          <div className="flex flex-col divide-y divide-border/60">
            <FactRow
              icon={MapPin}
              label="Country of Birth"
              value={str(client.country_of_birth)}
            />
            <FactRow
              icon={Flag}
              label="Country of Citizenship"
              value={str(client.country_of_citizenship)}
            />
            <FactRow
              icon={IdCard}
              label="Immigration Status"
              value={str(client.current_immigration_status)}
            />
            <FactRow
              icon={Calendar}
              label="Status Exp. Date"
              value={formatMdY(client.immigration_status_expiration, "-")}
              date={client.immigration_status_expiration}
            />
            <FactRow
              icon={Calendar}
              label="Priority Date"
              value={formatMdY(client.priority_date, "-")}
            />
            <FactRow
              icon={Tag}
              label="Preference Category"
              value={str(client.preference_category)}
            />
          </div>
        )}
      </SectionCard>

      <SectionCard
        className="flex-1"
        title="Expiration Dates"
        action={
          <EditButton
            onClick={() =>
              onOpenPersonalSection(PERSONAL_SECTION_IDS.expirationDates)
            }
          />
        }
      >
        {isLoading || !client ? (
          <Loading />
        ) : (
          <div className="flex flex-col divide-y divide-border/60">
            <FactRow
              icon={Calendar}
              label="EAD"
              value={formatMdY(client.ead_expiration, "-")}
              date={client.ead_expiration}
            />
            <FactRow
              icon={Calendar}
              label="Advance Parole"
              value={formatMdY(client.advance_parole_expiration, "-")}
              date={client.advance_parole_expiration}
            />
            <FactRow
              icon={Calendar}
              label="Passport"
              value={formatMdY(client.passport_expiration, "-")}
              date={client.passport_expiration}
            />
            <FactRow
              icon={Calendar}
              label="Visa"
              value={formatMdY(client.visa_expiration, "-")}
              date={client.visa_expiration}
            />
          </div>
        )}
      </SectionCard>
    </div>
  );
};
